import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} toggleSoundButton - The button to toggle sound
 * @property {HTMLElement} controlsBar - The controls bar container
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
	/** @type {boolean} */
	isPlaying = false;

	/** @type {boolean} */
	isMuted = true;

	#abortController = new AbortController();
	#gracePeriodTimeout = null;
	#controlsTimer = null;
	#autoLoadObserver = null;

	/** @param {boolean} isPlaying */
	#updatePausedClass(isPlaying) {
		this.classList.toggle('is-paused', !isPlaying);
	}

	#toggleControlsVisibility() {
		if (this.classList.contains('is-controls-visible')) {
			this.classList.remove('is-controls-visible');
		} else {
			this.classList.add('is-controls-visible');
			this.#resetControlsTimer();
		}
	}

	#resetControlsTimer() {
		clearTimeout(this.#controlsTimer);
		this.#controlsTimer = setTimeout(() => {
			if (this.isPlaying) {
				this.classList.remove('is-controls-visible');
			}
		}, 3000);
	}

	connectedCallback() {
		super.connectedCallback();
		const signal = this.#abortController.signal;
		// If we're to use deferred media for images, we will need to run this only when it's not an image type media
		document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), {
			signal,
		});
		window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

		// For autoplay videos that load inline (no poster click), reveal the controls bar.
		// The :scope > video guard ensures this only runs when the video is rendered inline,
		// not when it lives only in the <template> awaiting intersection-triggered load.
		if (
			this.hasAttribute('autoplay') &&
			this.refs.controlsBar &&
			this.querySelector(':scope > video')
		) {
			this.isPlaying = true;
			this.setAttribute('data-media-loaded', 'true');
			this.updatePlayPauseHint(true);
		}

		// For autoplay videos rendered only in <template>, defer load until the element
		// is near the viewport so below-fold videos don't race for bandwidth on page load.
		// The :scope > video/iframe guard prevents double-loading when a fallback inline
		// element already exists (e.g. when video_preview_image is blank).
		if (
			this.hasAttribute('autoplay') &&
			!this.getAttribute('data-media-loaded') &&
			!this.querySelector(':scope > video, :scope > iframe') &&
			!window.matchMedia('(prefers-reduced-motion: reduce)').matches
		) {
			this.#autoLoadObserver = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting) {
							this.loadContent(false, false);
							this.isPlaying = true;
							this.updatePlayPauseHint(true);
							this.#autoLoadObserver?.disconnect();
							this.#autoLoadObserver = null;
							return;
						}
					}
				},
				{ rootMargin: '200px 0px' }
			);
			this.#autoLoadObserver.observe(this);
		}

		// Click on video area (not controls) to toggle play/pause (desktop)
		// or show/hide controls (mobile)
		this.addEventListener(
			'click',
			(e) => {
				// Ignore clicks on control buttons or the poster button
				if (e.target.closest('.video-control-btn') || e.target.closest('.deferred-media__poster-button')) {
					if (window.matchMedia('(hover: none)').matches) {
						this.#resetControlsTimer();
					}
					return;
				}

				// Only act if media is already loaded (not the initial poster click)
				if (!this.getAttribute('data-media-loaded')) return;

				if (window.matchMedia('(hover: none)').matches) {
					// Mobile: toggle controls visibility
					this.#toggleControlsVisibility();
				} else {
					// Desktop: click video area to play/pause
					this.toggleMedia();
				}
			},
			{ signal }
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
		clearTimeout(this.#gracePeriodTimeout);
		clearTimeout(this.#controlsTimer);
		this.#autoLoadObserver?.disconnect();
	}

	/**
	 * Updates the visual hint for play/pause state
	 * @param {boolean} isPlaying - Whether the video is currently playing
	 */
	updatePlayPauseHint(isPlaying) {
		const controlsBar = this.refs.controlsBar;
		if (controlsBar instanceof HTMLElement) {
			controlsBar.classList.remove('hidden');
		}
		const toggleMediaButton = this.refs.toggleMediaButton;
		if (toggleMediaButton instanceof HTMLElement) {
			const playIcon = toggleMediaButton.querySelector('.icon-play');
			if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
			const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
			if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
		}
		this.#updatePausedClass(isPlaying);
	}

	/**
	 * Shows the deferred media content
	 */
	showDeferredMedia = () => {
		this.loadContent(true);
		this.isPlaying = true;
		this.updatePlayPauseHint(this.isPlaying);

		// User clicked play — unmute if sound is enabled (safe since we have user gesture)
		if (this.hasAttribute('data-enable-sound')) {
			const video = this.querySelector('video');
			if (video) {
				video.muted = false;
				this.isMuted = false;
				this.#updateSoundHint();
			}
		}

		// Grace period: suppress controls appearing immediately after clicking poster
		this.classList.add('is-grace-period');
		this.#gracePeriodTimeout = setTimeout(() => {
			this.classList.remove('is-grace-period');
		}, 1500);
	};

	/**
	 * Loads the content
	 * @param {boolean} [focus] - Whether to focus the content
	 * @param {boolean} [dispatchStartEvent] - Whether to dispatch MediaStartedPlayingEvent
	 *   (which pauses sibling media). False for ambient/background videos that should
	 *   coexist with other playing media.
	 */
	loadContent(focus = true, dispatchStartEvent = true) {
		if (this.getAttribute('data-media-loaded')) return;

		if (dispatchStartEvent) {
			this.dispatchEvent(new MediaStartedPlayingEvent(this));
		}

		const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

		if (!content) return;

		this.setAttribute('data-media-loaded', 'true');
		this.appendChild(content);

		if (focus && content instanceof HTMLElement) {
			content.focus();
		}

		this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

		if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
			// force autoplay for safari
			content.play();
		}
	}

	/**
	 * Toggle play/pause state of the media
	 */
	toggleMedia() {
		if (this.isPlaying) {
			this.pauseMedia();
		} else {
			this.playMedia();
		}
	}

	playMedia() {
		/** @type {HTMLIFrameElement | null} */
		const iframe = this.querySelector('iframe[data-video-type]');
		if (iframe) {
			iframe.contentWindow?.postMessage(
				iframe.dataset.videoType === 'youtube'
					? '{"event":"command","func":"playVideo","args":""}'
					: '{"method":"play"}',
				'*'
			);
		} else {
			this.querySelector('video')?.play();
		}
		this.isPlaying = true;
		this.updatePlayPauseHint(this.isPlaying);
	}

	/**
	 * Pauses the media
	 */
	pauseMedia() {
		/** @type {HTMLIFrameElement | null} */
		const iframe = this.querySelector('iframe[data-video-type]');

		if (iframe) {
			iframe.contentWindow?.postMessage(
				iframe.dataset.videoType === 'youtube'
					? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
					: '{"method":"pause"}',
				'*'
			);
		} else {
			this.querySelector('video')?.pause();
		}
		this.isPlaying = false;

		// If we've already revealed the deferred media, we should toggle the play/pause hint
		if (this.getAttribute('data-media-loaded')) {
			this.updatePlayPauseHint(this.isPlaying);
		}
	}

	/**
	 * Toggles sound on/off for HTML5 video elements
	 */
	toggleSound() {
		const video = this.querySelector('video');
		if (!video) return;

		this.isMuted = !this.isMuted;
		video.muted = this.isMuted;
		this.#updateSoundHint();
	}

	#updateSoundHint() {
		const btn = this.refs.toggleSoundButton;
		if (!btn) return;

		const volumeOn = btn.querySelector('.icon-volume-on');
		const volumeOff = btn.querySelector('.icon-volume-off');
		if (volumeOn) volumeOn.classList.toggle('hidden', this.isMuted);
		if (volumeOff) volumeOff.classList.toggle('hidden', !this.isMuted);
	}
}

if (!customElements.get('deferred-media')) {
	customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
	#abortController = new AbortController();

	loadContent() {
		super.loadContent();

		Shopify.loadFeatures([
			{
				name: 'model-viewer-ui',
				version: '1.0',
				onLoad: this.setupModelViewerUI.bind(this),
			},
		]);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}

	pauseMedia() {
		super.pauseMedia();
		this.modelViewerUI?.pause();
	}

	playMedia() {
		super.playMedia();
		this.modelViewerUI?.play();
	}

	/**
	 * @param {Error[]} errors
	 */
	async setupModelViewerUI(errors) {
		if (errors) return;

		if (!Shopify.ModelViewerUI) {
			await this.#waitForModelViewerUI();
		}

		if (!Shopify.ModelViewerUI) return;

		const element = this.querySelector('model-viewer');
		if (!element) return;

		const signal = this.#abortController.signal;

		this.modelViewerUI = new Shopify.ModelViewerUI(element);
		if (!this.modelViewerUI) return;

		this.playMedia();

		// Track pointer events to detect taps
		let pointerStartX = 0;
		let pointerStartY = 0;

		element.addEventListener(
			'pointerdown',
			(/** @type {PointerEvent} */ event) => {
				pointerStartX = event.clientX;
				pointerStartY = event.clientY;
			},
			{ signal }
		);

		element.addEventListener(
			'click',
			(/** @type {PointerEvent} */ event) => {
				const distanceX = Math.abs(event.clientX - pointerStartX);
				const distanceY = Math.abs(event.clientY - pointerStartY);
				const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

				// Try to ensure that this is a tap, not a drag.
				if (totalDistance < 10) {
					// When the model is paused, it has its own button overlay for playing the model again.
					// If we're receiving a click event, it means the model is playing, all we can do is pause it.
					this.pauseMedia();
				}
			},
			{ signal }
		);
	}

	/**
	 * Waits for Shopify.ModelViewerUI to be defined.
	 * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
	 * @returns {Promise<void>}
	 */
	async #waitForModelViewerUI() {
		const maxAttempts = 10;
		const interval = 50;

		for (let i = 0; i < maxAttempts; i++) {
			if (Shopify.ModelViewerUI) {
				return;
			}
			await new Promise((resolve) => setTimeout(resolve, interval));
		}
	}
}

if (!customElements.get('product-model')) {
	customElements.define('product-model', ProductModel);
}
